import { useNavigate } from '@tanstack/react-router'
import { Monitor, Moon, Search, Sun } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useTheme } from '@/shared/theme/theme-provider'
import { Button } from '@/shared/ui/button'
import {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/shared/ui/command'

import { flattenNavItems, type NavItem } from './navigation'

interface CommandMenuProps {
  /** Itens do menu já filtrados pela permissão do admin. */
  items: NavItem[]
}

/** Agrupa itens consecutivos da mesma seção, na ordem do menu lateral. */
function groupItems(items: NavItem[]): { group?: NavItem['group']; items: NavItem[] }[] {
  const groups: { group?: NavItem['group']; items: NavItem[] }[] = []

  for (const item of items) {
    const last = groups.at(-1)
    if (last && last.group === item.group) last.items.push(item)
    else groups.push({ group: item.group, items: [item] })
  }

  return groups
}

/**
 * Busca global do topo (Ctrl K / ⌘ K): vai para qualquer página do menu
 * e troca o tema sem sair do teclado.
 */
export function CommandMenu({ items }: CommandMenuProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { setTheme } = useTheme()
  const [open, setOpen] = useState(false)

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() === 'k' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault()
        setOpen((value) => !value)
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [])

  const run = (action: () => unknown) => {
    setOpen(false)
    void action()
  }

  return (
    <>
      <Button
        variant="outline"
        onClick={() => setOpen(true)}
        className="hidden h-10 w-64 justify-start gap-2.5 border-border bg-background px-3 font-normal text-muted-foreground sm:flex xl:w-80"
      >
        <Search aria-hidden />
        {t('search.trigger')}
        <kbd className="ml-auto rounded-sm border bg-card px-1.5 font-sans text-[11px] font-semibold">
          {t('search.shortcut')}
        </kbd>
      </Button>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setOpen(true)}
        className="sm:hidden"
        aria-label={t('search.trigger')}
      >
        <Search />
      </Button>

      <CommandDialog
        open={open}
        onOpenChange={setOpen}
        title={t('search.title')}
        description={t('search.description')}
      >
        <Command>
          <CommandInput placeholder={t('search.placeholder')} />
          <CommandList className="max-h-[360px]">
            <CommandEmpty>{t('search.empty')}</CommandEmpty>
            {groupItems(flattenNavItems(items)).map(({ group, items: sectionItems }) => (
              <CommandGroup key={group ?? sectionItems[0]?.to} heading={group ? t(group) : t('search.pages')}>
                {sectionItems.map((item) => (
                  <CommandItem
                    key={item.to}
                    value={`${t(item.labelKey)} ${item.to}`}
                    onSelect={() => run(() => navigate({ to: item.to }))}
                  >
                    <item.icon aria-hidden />
                    {t(item.labelKey)}
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
            <CommandSeparator />
            <CommandGroup heading={t('theme.label')}>
              <CommandItem value={t('theme.light')} onSelect={() => run(() => setTheme('light'))}>
                <Sun aria-hidden />
                {t('theme.light')}
              </CommandItem>
              <CommandItem value={t('theme.dark')} onSelect={() => run(() => setTheme('dark'))}>
                <Moon aria-hidden />
                {t('theme.dark')}
              </CommandItem>
              <CommandItem value={t('theme.system')} onSelect={() => run(() => setTheme('system'))}>
                <Monitor aria-hidden />
                {t('theme.system')}
              </CommandItem>
            </CommandGroup>
          </CommandList>
        </Command>
      </CommandDialog>
    </>
  )
}
